/**
 * Дерево приложения: сокет → стор → фильтры и лента.
 *
 * Живёт отдельно от точки входа ради одной проверки: белый экран. Приложение,
 * собранное только в `main.tsx`, невозможно смонтировать в тесте — и падение при
 * первом же рендере доезжает до прода зелёным, потому что сервер отдаёт 200 и
 * бандл на месте, а `#root` пуст. Тест на отрисовку лежит в `App.test.tsx`.
 *
 * Здесь же единственное место, где живёт версия протокола на клиенте. Сервер, ушедший
 * на другую версию, не пытается быть понятым: клиент перезагружается сам. Иначе
 * вкладка, открытая месяц назад, молча показывала бы числа по старым правилам.
 */

import { useEffect } from 'react';

import { APP_LABEL, apiUrl, feedSocketUrl } from '../lib/build';
import type { Middle } from '../lib/protocol';
import { type SourceState, sourceView, useFeed } from '../store/feed';
import { Feed } from './Feed';
import { Filters } from './Filters';
import { FeedClient } from '../ws/client';

const PROTO = Number(import.meta.env.VITE_PROTO_VERSION ?? 1);
/** Как часто спрашивать состояние источников. Свежесть меняется медленнее ленты. */
const META_INTERVAL = 30_000;

/**
 * Источник из ответа сервера. Поля задержки необязательны намеренно: во время
 * выката несколько секунд отвечает предыдущая версия API, а откат возвращает её
 * насовсем — читать их как обязательные значило бы падать на первом же кадре.
 */
function asSource(raw: unknown): SourceState {
  const item = (raw ?? {}) as Record<string, unknown>;
  const lag = item.ageSeconds;
  const limit = item.staleAfterSeconds;
  return {
    code: String(item.code ?? '—'),
    stale: item.stale === true,
    quotes: typeof item.quotes === 'number' ? item.quotes : 0,
    lagSec: typeof lag === 'number' ? lag : null,
    ...(typeof limit === 'number' ? { staleAfterSec: limit } : {}),
  };
}

const STATUS_TEXT: Record<string, string> = {
  connecting: 'соединение',
  live: 'в эфире',
  reconnecting: 'переподключение',
  paused: 'обновляемся',
};

export function App() {
  const status = useFeed((s) => s.status);
  const active = useFeed((s) => s.activeForks);
  const paused = useFeed((s) => s.paused);
  const sources = useFeed((s) => s.sources);
  const latencyMs = useFeed((s) => s.latencyMs);

  useEffect(() => {
    const store = useFeed.getState();
    const client = new FeedClient(feedSocketUrl(location), PROTO, {
      onStatus: store.setStatus,
      onState: store.apply,
      onProtocolMismatch: (serverProto) => {
        console.warn(`протокол сервера ${serverProto}, у нас ${PROTO} — перезагрузка`);
        location.reload();
      },
    });
    client.connect();

    // Состояние источников идёт отдельным запросом, а не через сокет: пустая лента
    // при живом соединении означает разное — спокойный рынок, выключенный движок или
    // молчащий источник, — и путать это нельзя.
    const meta = async () => {
      try {
        const body = (await (await fetch(apiUrl('/v1/meta'))).json()) as {
          sources?: unknown;
          latency?: { oursP50Ms?: unknown };
          antiscam?: unknown;
        };
        if (Array.isArray(body.sources)) store.setSources(body.sources.map(asSource));
        const p50 = body.latency?.oursP50Ms;
        store.setLatency(typeof p50 === 'number' ? p50 : null);
        // Легенда правил анти-скама: коды заводит бэкенд, и объяснить их клиент
        // обязан без своей выкатки. Старый сервер её не отдаёт — тогда пусто.
        if (body.antiscam !== null && typeof body.antiscam === 'object') {
          store.setRuleCatalog(body.antiscam as Record<string, string>);
        }
      } catch {
        // Сеть отвалилась — состояние источников оставляем прежним: показать
        // «движок выключен» из-за одного неудачного запроса было бы ложной тревогой.
      }
    };
    // Полувилки идут снапшотом по REST, а не дельтой через сокет. Жанр живёт
    // минутами: окно между линиями не съедается так, как съедается вилка, — и
    // второй дельта-канал ради него был бы платой сложностью без выигрыша.
    const middles = async () => {
      try {
        const body = (await (await fetch(apiUrl('/v1/middles'))).json()) as {
          middles?: unknown;
        };
        if (Array.isArray(body.middles)) store.setMiddles(body.middles as Middle[]);
      } catch {
        // Прошлый список остаётся на месте: очистить ленту из-за одного
        // неудачного запроса значило бы показать пустой рынок вместо сбоя сети.
      }
    };

    void meta();
    void middles();
    const timer = setInterval(() => {
      void meta();
      void middles();
    }, META_INTERVAL);

    return () => {
      clearInterval(timer);
      client.close();
    };
  }, []);

  return (
    <main className="shell">
      <div className="top">
        {/* Подпись контура. У боевой сборки её нет, у второго контура она
            обязательна: две одинаковые с виду вкладки с разными лентами — прямой
            способ поставить по цене из чужого источника. */}
        <h1 className="brand">
          FORQ{APP_LABEL ? ` ${APP_LABEL}` : ''}
          <span>·</span>
        </h1>
        <div className="meta">
          <span>
            <i className={`dot ${status}`} />
            {STATUS_TEXT[status] ?? status}
          </span>
          <span className="num">вилок: {active}</span>
          {/* Замеренная медиана, а не обещание из лендинга. Пока выборки мало,
              строки нет вовсе: число, взятое из воздуха, здесь хуже пустоты. */}
          {latencyMs !== null && (
            <span
              className="num"
              title="Медиана пути от котировки площадки до кадра в этой вкладке"
            >
              отклик: {latencyMs} мс
            </span>
          )}
          {/* Молчащий источник называется по имени: «частично работает» на экране
              выглядит как «работает», и по такой ленте начинают ставить. Рядом —
              возраст последней котировки: между «отдаёт цены» и «протух» лежит
              состояние, в котором площадка формально жива, а ставку по её цене
              уже не примут. */}
          {sources.map((source) => {
            const view = sourceView(source);
            return (
              <span key={source.code} className={`src ${view.mood}`} title={view.hint}>
                {source.code}: {view.text}
              </span>
            );
          })}
          {paused && <span>лента на паузе</span>}
          <span>протокол {PROTO}</span>
        </div>
      </div>
      <Filters />
      <Feed />
    </main>
  );
}
